/* eslint-disable react-hooks/exhaustive-deps */
"use client";
import { useEffect, useState } from "react";
import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { LocationState, PickupFormData } from "../../../types/types";
import {
  useFetchCountriesAllData,
  useFetchCountryCitiesAllData,
} from "../../../apis/locationApis";
import { useMerchantAllListData } from "../../../apis/merchantApis";
import NewSelectInput from "../../../components/NewSelectInput";
import NewinputField from "../../../components/NewInput";
import PickupAddressPicker from "./PickupAddressPicker";
import { IconLocation } from "@tabler/icons-react";

interface CreateLocationProps {
  register: UseFormRegister<PickupFormData>;
  setValue: UseFormSetValue<PickupFormData>;
  watch: UseFormWatch<PickupFormData>;
  errors: FieldErrors<PickupFormData>;
  clearErrors: UseFormClearErrors<PickupFormData>;
  control: Control<PickupFormData>;
  reset?: any;
  className?: string;
  mode?: string;
  defaultValues?: PickupFormData;
  heightClass?: string;
  hideMerchantSelect?: boolean;
}

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      {title}
    </p>
  </div>
);

const CreateLocation: React.FC<CreateLocationProps> = ({
  register,
  errors,
  setValue,
  watch,
  clearErrors,
  control,
  mode,
  defaultValues,
  reset,
  heightClass,
  hideMerchantSelect = false,
}) => {
  const [createLocation, setCreateLocation] = useState<LocationState>({});
  const { data: countries = [] } = useFetchCountriesAllData();
  const { data: clients } = useMerchantAllListData();

  const {
    data: cities = [],
    refetch: fetchReceiverCities,
    isFetching: isFetchingCities,
  } = useFetchCountryCitiesAllData(createLocation?.country?.id);

  const isView = mode === "view";

  useEffect(() => {
    if (createLocation?.country?.id) {
      fetchReceiverCities();
    }
  }, [createLocation?.country?.id]);

  useEffect(() => {
    if (defaultValues) reset(defaultValues);
  }, [defaultValues, reset]);

  // Sync initial/default values with local state
  useEffect(() => {
    const countryId = watch("countryId");
    const cityId = watch("cityId");
    const districtId = watch("districtId");

    if (countryId && countries.length > 0) {
      const country = countries.find((c: any) => c.id === countryId);
      if (country) {
        setCreateLocation((prev) => ({
          ...prev,
          country: { id: country.id, name: country.name },
        }));
      }
    }

    if (cityId && cities.length > 0) {
      const city = cities.find((c: any) => c.id === cityId);
      if (city) {
        setCreateLocation((prev) => ({
          ...prev,
          city: { id: city.id, name: city.name },
        }));
      }
    }

    if (districtId) {
      // We don't have district name easily unless it's in a list,
      // but the picker handles the mapping.
      // We'll just set the ID to trigger the watch if needed.
      setCreateLocation((prev) => ({
        ...prev,
        district: { id: districtId, name: watch("districtId") }, // placeholder or name
      }));
    }
  }, [watch("countryId"), watch("cityId"), countries, cities]);

  return (
    <div className="w-full space-y-4 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-tr-[30px]">
      <div
        className={` ${heightClass || "h-[calc(100vh-10rem)]"} overflow-y-auto space-y-4   p-4   scrollbar-clean`}
      >
        <div className="space-y-3 bg-white/40 rounded-2xl p-4 shadow-sm">
          <SectionHeader icon={IconLocation} title="Location" />

          {!hideMerchantSelect ? (
            <NewSelectInput
              label="Select Merchant"
              className="flex items-center justify-between"
              labelClass="w-full"
              name="forClientId"
              value={watch("forClientId")}
              register={register}
              clearErrors={clearErrors}
              error={errors?.forClientId}
              validation={
                mode === "add"
                  ? { required: "Please select Merchant" }
                  : undefined
              }
              onChange={(val) => {
                if (mode === "add") {
                  setValue("forClientId", val);
                }
              }}
              selectOptions={
                clients?.map((client: any) => ({
                  id: client.id,
                  value: client.id,
                  text: client.name,
                })) || []
              }
              disabled={mode === "edit" || mode === "view"}
            />
          ) : (
            <input type="hidden" {...register("forClientId")} />
          )}

          <NewinputField
            className={
              hideMerchantSelect
                ? "flex items-center justify-between"
                : "flex items-center justify-between"
            }
            labelClass="w-full"
            name="locationName"
            type="text"
            title="Location Name"
            placeholder="Enter Location Name"
            register={register}
            error={errors.locationName}
            validation={{
              required: "Location Name is required",
              pattern: {
                value: /^(?=.*[A-Za-z])[A-Za-z0-9\s-]+$/,
                message:
                  "Location Name must include at least one letter (not only numbers)",
              },
              validate: (value) => {
                if (!value?.trim())
                  return "Location Name cannot be empty or only spaces";
                return true;
              },
            }}
            disabled={isView}
          />

          <PickupAddressPicker
            register={register}
            errors={errors}
            setValue={setValue}
            control={control}
            clearErrors={clearErrors}
            countries={countries}
            cities={cities}
            selectedCountry={createLocation.country}
            selectedCity={createLocation.city}
            selectedDistrict={createLocation.district}
            onCountryChange={(country) =>
              setCreateLocation((prev) => ({
                ...prev,
                country,
                city: undefined,
                district: undefined,
              }))
            }
            onCityChange={(city) =>
              setCreateLocation((prev) => ({
                ...prev,
                city,
                district: undefined,
              }))
            }
            onDistrictChange={(district) =>
              setCreateLocation((prev) => ({ ...prev, district }))
            }
            mode={mode}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateLocation;
